import { useMutation } from '@apollo/client'
import { Button, Checkbox, Divider, Form, Input, message, Modal } from 'antd'
import { useForm } from 'antd/lib/form/Form'
import gql from 'graphql-tag'
import { Maybe } from 'graphql/jsutils/Maybe'
import React, { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { EVENEMENT_ADD_MUTATION } from '../../apollo/mutation/evenement.mutation'
import { EVENEMENTS } from '../../apollo/query/demande.query'
import { AWS_S3_RESIZED_URL, AWS_S3_URL } from '../../constants'
import { useImperativeQuery } from '../../helper'
import { useAuth, useQueryCopro } from '../../lib/hooks'
import {
  D4_Demande_Estimation,
  D4_Demande_Estimation_Insert_Input,
  D4_Estimation_Insert_Input,
  D4_Evenementdemande_Set_Input,
  D4_Fn_View_Demande,
  D4_Fn_View_Evenements,
  D4_Personne,
  D4_Societe,
} from '../../types'
import MsGraphAPI, { useMsGraphAPI } from '../Courriel/ms-graph.api'
import Thumbnail from '../Thumbnail'
import DebounceSelect from './DebounceSelect'

const COURRIEL_DESTINATAIRES_QUERY = gql`
  query CourrielDestinataires($trg: String!, $search: String!) {
    gestionnaires: d4_personne(
      where: {
        _and: [
          { dtype: { _eq: "Gestionnaire" } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
    }
    personnes: d4_personne(
      where: {
        _and: [
          { coproprietaire: { lot_coproprietaires: { lot: { copropriete: { trigramme: { _eq: $trg } } } } } }
          { mail1: { _neq: "" } }
          { _or: [{ nom: { _ilike: $search } }, { prenom: { _ilike: $search } }, { mail1: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      nom
      prenom
      mail1
    }
    fournisseurs: d4_societe(
      where: {
        _and: [
          { mail1: { _neq: "" } }
          { _or: [{ mail1: { _ilike: $search } }, { raisonsociale: { _ilike: $search } }] }
        ]
      }
      limit: 10
    ) {
      id
      raisonsociale
      mail1
    }
  }
`

const DEMANDE_ESTIMATION_MUTATION = gql`
  mutation DemandeEstimation($data: d4_demande_estimation_insert_input!) {
    demandeEstimation: insert_d4_demande_estimation_one(object: $data) {
      id
      estimations {
        id
      }
    }
  }
`

interface Props {
  onOk: (() => void) | undefined
  onCancel: (() => void) | undefined
  visible: boolean
  evenements: Maybe<D4_Fn_View_Evenements[]>
  demande: D4_Fn_View_Demande
}

// `${demande.description} (${demande.id})`
interface SendMailData {
  body: string
  subject: string
  to: string
  cc?: string[]
}

// let isImg = (texte: string) => /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(texte ?? '')
let isPdf = (texte: string) => /\.(pdf)$/i.test(texte ?? '')

const generateBody = ({
  body,
  evenements,
  token,
}: {
  body: any
  evenements: D4_Fn_View_Evenements[]
  token: string
}) => {
  return (
    body.replaceAll('\n', '<br />') +
    '<br/><br/>' +
    `<a href="https://visionv2.d4immobilier.fr/public/test/devis?token=${token}" style="font-size: 1.25rem; font-style: bold;" target="_blank" >ouvrir la demande d'estimation</a><br/><br/>` +
    evenements
      ?.map(
        (ev) =>
          `<div>${ev.auteurnom} - ${ev.auteurprenom}<br />${
            ev.fichier
              ? `<a href="${AWS_S3_URL}${ev.texte}" target="_blank" >
                    <img width="150px" src="${AWS_S3_RESIZED_URL}resized-${ev.texte}${
                  isPdf(ev.texte ?? '') ? '.png' : ''
                }" />
                  </a>`
              : ev.texte
          }</div><hr><br />`
      )
      .join('')
  )
}

const sendEmail = async ({ subject, body, to, cc }: SendMailData) => {
  const data = {
    message: {
      subject: subject,
      body: {
        contentType: 'html',
        content: body,
      },
      toRecipients: [{ emailAddress: { address: to.trim().toLocaleLowerCase() } }],
      ccRecipients: cc?.map((personne) => ({ emailAddress: { address: personne.trim().toLocaleLowerCase() } })) ?? [],
    },
    saveToSentItems: 'true',
  }

  const res = await MsGraphAPI.sendEmail({ data })
  // const res = null
  return { res, data }
}

const ModalDemandeEstimation: React.FC<Props> = ({ visible, onOk, onCancel, evenements, demande }) => {
  const [form] = useForm()
  const {
    user: {
      personne: { nom, prenom, id },
    },
  } = useAuth()
  const [createEvenement] = useMutation<{}, { data: D4_Evenementdemande_Set_Input }>(EVENEMENT_ADD_MUTATION)
  const [options, setOptions] = useState<any>()

  const [createDemandeEstimation] = useMutation<
    { demandeEstimation: D4_Demande_Estimation },
    { data: D4_Demande_Estimation_Insert_Input }
  >(DEMANDE_ESTIMATION_MUTATION)

  const search = useImperativeQuery<
    { personnes: D4_Personne[]; fournisseurs: D4_Societe[]; gestionnaires: D4_Personne[] },
    { trg: string; search: string }
  >(COURRIEL_DESTINATAIRES_QUERY)

  const [copro] = useQueryCopro(demande.coproprieteid)

  useMsGraphAPI()

  const handleFinish = useCallback(
    async (values: any) => {
      const tokens = values.to.map((t: string) => ({ token: uuidv4(), to: t }))

      const sendMailPromises = tokens.map(async (token: any) => {
        try {
          const content = generateBody({
            body: values.body,
            evenements: evenements?.filter((ev) => values.evt.includes(ev.id)) ?? ([] as D4_Fn_View_Evenements[]),
            token: token.token,
          })
          const estimations: D4_Estimation_Insert_Input = {
            mail: content,
            token: token.token,
            fournisseur_id: options.find((op: any) => op.value === token.to).key,
          }

          const demandeEstimation: { data: D4_Demande_Estimation_Insert_Input } = {
            data: {
              demande_id: demande.id,
              description: content,
              gestionnaire_id: id,
              estimations: { data: [estimations] },
            },
          }

          createDemandeEstimation({ variables: demandeEstimation })

          const data: SendMailData = {
            cc: values.cc,
            subject: values.subject,
            body: content,
            to: token.to,
          }

          return sendEmail(data)
        } catch (error) {
          message.error("Erreur lors de la création de la demande d'estimation")
        }
      })

      try {
        await Promise.all(sendMailPromises)
        message.success("La demande d'estimation a bien été envoyé")
        form.resetFields()
        createEvenement({
          variables: {
            data: {
              demande_id: demande.id,
              texte: `Envoi d'une demande d'estimation\n\n<strong>De : ${nom} ${prenom}\nA : ${
                values.to?.join(', ') ?? ''
              }\nCc : ${values.cc?.join(', ') ?? ''}\nObjet : ${values.subject}</strong>\n\n${values.body}`,
            },
          },
          refetchQueries: [
            {
              query: EVENEMENTS,
              variables: { id: demande.id },
            },
          ],
        })
        onOk?.()
      } catch (error) {
        message.error("Une erreur est survenue lors de l'envoi du mail")
      }
    },
    [createDemandeEstimation, createEvenement, demande.id, evenements, form, id, nom, onOk, options, prenom]
  )

  const handleSearch = useCallback(
    async (v: string): Promise<any[]> => {
      if (!v || v.length < 3) return Promise.resolve([])
      const res = await search({ trg: copro?.trigramme ?? '', search: '%' + v + '%' })
      const nextOptions = []

      res.data.fournisseurs.length > 0 &&
        nextOptions.push({
          key: 'fournisseurs',
          label: 'Fournisseurs',
          options: res.data.fournisseurs.map((four) => ({
            value: four.mail1,
            label: four.raisonsociale,
            key: four.id,
          })),
        })

      setOptions(nextOptions?.[0]?.options)
      return Promise.resolve(nextOptions)
    },
    [copro?.trigramme, search]
  )

  const optionsFilter = (fieldName: string) => (options: OptionsType) => {
    return options.map((opt) => ({
      ...opt,
      options: opt.options?.filter((o) => !form.getFieldValue(fieldName)?.includes(o.value)),
    }))
  }

  return (
    <Modal
      title="Demande D'estimation"
      visible={visible}
      footer={false}
      onCancel={onCancel}
      cancelText="Annuler"
      width="66%"
    >
      <Form
        form={form}
        onFinish={handleFinish}
        labelCol={{ span: 2 }}
        initialValues={{
          body: 'Bonjour,\n\nSincères Salutations\n' + prenom + ' ' + nom,
          evt: [],
          subject:
            `Demande d'estimation: ${copro?.nom} > ${demande.equipementlibelle} > ${demande.description}`.trim() +
            ` (${demande.id})`,
        }}
      >
        <Form.Item name="to" label="A">
          <DebounceSelect
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('to')}
          />
        </Form.Item>
        <Form.Item name="cc" label="CC">
          <DebounceSelect
            placeholder="Recherche"
            mode="tags"
            fetchOptions={handleSearch}
            optionsFilter={optionsFilter('cc')}
          />
        </Form.Item>
        <Form.Item name="subject" label="Objet">
          <Input />
        </Form.Item>
        <Form.Item name="body" label="Message">
          <Input.TextArea rows={6} />
        </Form.Item>
        <Form.Item name="evt">
          <Checkbox.Group>
            {evenements?.map((evt) => (
              <>
                <Checkbox value={evt.id} key={evt.id} style={{ marginLeft: 0 }}>
                  {evt.fichier ? (
                    <>
                      {evt.texte}
                      <Thumbnail texte={evt.texte} />
                    </>
                  ) : (
                    <div dangerouslySetInnerHTML={{ __html: evt.texte?.trim() ?? '' }} />
                  )}
                </Checkbox>
                <Divider />
              </>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 22 }}>
          <Button htmlType="submit" type="primary">
            Envoyer
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ModalDemandeEstimation
